import React, { useState } from 'react';
import {
  Sparkles,
  Layers,
  Check,
  Loader2,
  Ship,
  Building2,
  Zap,
} from 'lucide-react';
import { SectionType, PageSection, IndustryType } from '../../types';
import { aiClientService } from '../../services/aiService';

interface AISectionGeneratorModalProps {
  industry: IndustryType;
  siteName: string;
  onClose: () => void;
  onSectionGenerated: (section: PageSection) => void;
}

const SECTION_TYPE_OPTIONS: { id: SectionType; title: string; desc: string }[] = [
  { id: 'HERO', title: 'Hero (Giriş & Karşılama)', desc: 'Büyük başlık, açıklama, CTA butonu ve arkaplan görseli.' },
  { id: 'SERVICES', title: 'Hizmetler & Özellikler', desc: 'Kartlar halinde hizmet veya avantaj listesi.' },
  { id: 'ABOUT', title: 'Kurumsal / Hakkımızda', desc: 'Hikaye, misyon, görsel ve detaylı bilgi.' },
  { id: 'STATS', title: 'İstatistik & Başarı Rakamları', desc: '4 sütunlu vurgulu rakamlar ve başarı sayaçları.' },
  { id: 'TESTIMONIALS', title: 'Müşteri Yorumları & Referans', desc: 'Müşteri deneyimleri ve puanlama kartları.' },
  { id: 'FAQ', title: 'Sıkça Sorulan Sorular (SSS)', desc: 'Açılır kapanır akordeon soru-cevap listesi.' },
  { id: 'PRICING', title: 'Fiyatlandırma Tablosu', desc: 'Planlar, özellikler ve satın alma butonları.' },
  { id: 'RESERVATION_CTA', title: 'Hızlı Rezervasyon / CTA', desc: 'Vurgulu eylem çağrısı ve rezervasyon paneli.' },
  { id: 'CONTACT', title: 'İletişim & Harita Formu', desc: 'İletişim formu, adres ve telefon bilgileri.' },
];

export const AISectionGeneratorModal: React.FC<AISectionGeneratorModalProps> = ({
  industry,
  siteName,
  onClose,
  onSectionGenerated,
}) => {
  const [selectedType, setSelectedType] = useState<SectionType>('HERO');
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const generated = await aiClientService.generateSection(
        selectedType,
        prompt || `${siteName} için ${selectedType} bölümü`,
        industry,
        siteName
      );
      onSectionGenerated(generated);
      onClose();
    } catch (err) {
      console.error('Section generate failed:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl bg-slate-900 border border-slate-700/80 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 bg-slate-950 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center text-white">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">AI ile Yeni Bölüm Üret</h3>
              <p className="text-xs text-slate-400">
                {siteName} ({industry === 'YACHT_RENTAL' ? 'Yat Kiralama' : 'Kurumsal'})
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-800 transition"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          <div>
            <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2 block">
              1. Bölüm Türü Seçin:
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SECTION_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setSelectedType(opt.id)}
                  className={`p-3 rounded-xl border text-left transition ${
                    selectedType === opt.id
                      ? 'border-indigo-500 bg-indigo-950/40 text-white'
                      : 'border-slate-800 bg-slate-950 text-slate-400 hover:bg-slate-800'
                  }`}
                >
                  <div className="text-xs font-bold text-white mb-0.5">{opt.title}</div>
                  <div className="text-[11px] text-slate-400 line-clamp-1">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5 block">
              2. Bölüm İhtiyacınızı Belirtin (İsteğe Bağlı Prompt):
            </label>
            <textarea
              rows={3}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Örn: Mavi turda özel aşçı menüleri ve koylarda su sporları aktivitelerini vurgulayan çekici bir bölüm hazırla..."
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-white placeholder-slate-500 text-xs focus:outline-none focus:border-indigo-500"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-950 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-slate-400 hover:text-white transition"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerating}
            className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold flex items-center gap-2 transition disabled:opacity-50 shadow-lg shadow-indigo-600/30"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Bölüm Üretiliyor...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                AI ile Oluştur & Sayfaya Ekle
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AISectionGeneratorModal;
